import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAiCommandPalette } from '../context/AiCommandPaletteContext';
import StatusBotIcon from '../../StatusBotIcon';
import { useAuth } from '../../../context/AuthContext';
import { useAiPageContext, DEFAULT_LOADING_TEXTS } from '../../../context/AiPageContextProvider';

// Loading message component with shimmer animation
const LoadingMessage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shownOnceMessages, setShownOnceMessages] = useState(new Set());
  const { currentAiPageContext } = useAiPageContext();
  
  // Get loading text config from context or use default
  const loadingTextConfig = currentAiPageContext?.loadingTexts || DEFAULT_LOADING_TEXTS;

  // Function to get the next valid index
  const getNextIndex = (currentIdx) => {
    // Loop through indices starting from the next one
    for (let i = 1; i <= loadingTextConfig.length; i++) {
      // Calculate the next index with wrap-around
      const nextIdx = (currentIdx + i) % loadingTextConfig.length;
      const config = loadingTextConfig[nextIdx];
      
      // Skip if it's a showOnce message that's already been shown
      if (config.showOnce && shownOnceMessages.has(nextIdx)) {
        continue;
      }
      
      return nextIdx;
    }
    
    // If all showOnce messages have been shown and there are no regular messages,
    // reset and use the first non-showOnce message or the first message if all are showOnce
    setShownOnceMessages(new Set());
    return 0;
  };

  useEffect(() => {
    let timeoutId;
    
    const scheduleNextMessage = () => {
      // Get next valid index
      const nextIndex = getNextIndex(currentIndex);
      
      // Mark as shown if it's a showOnce message
      if (loadingTextConfig[nextIndex].showOnce) {
        setShownOnceMessages(prev => new Set([...prev, nextIndex]));
      }
      
      // Update current index
      setCurrentIndex(nextIndex);
      
      // Calculate random duration based on min/max
      const { minTime, maxTime } = loadingTextConfig[nextIndex];
      const duration = Math.floor(Math.random() * (maxTime - minTime) + minTime);
      
      // Schedule next change
      timeoutId = setTimeout(scheduleNextMessage, duration);
    };
    
    // Calculate initial duration
    const { minTime, maxTime } = loadingTextConfig[currentIndex];
    const initialDuration = Math.floor(Math.random() * (maxTime - minTime) + minTime);
    
    // If first message is showOnce, mark it as shown
    if (loadingTextConfig[currentIndex].showOnce) {
      setShownOnceMessages(prev => new Set([...prev, currentIndex]));
    }
    
    // Schedule first change
    timeoutId = setTimeout(scheduleNextMessage, initialDuration);
    
    return () => clearTimeout(timeoutId);
  }, [currentIndex, loadingTextConfig]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <span className="relative inline-block bg-radial from-black/40 from-40% to-black/80 to-60% dark:from-white/40 dark:from-40% dark:to-white/80 dark:to-60% bg-clip-text text-transparent animate-shimmer max-w-full truncate">
          {loadingTextConfig[currentIndex].text}
        </span>
      </motion.div>
    </AnimatePresence>
  );
};

// Premium-styled task card component for AI results
const TaskLinkCard = ({ href, taskKey, summary, status, assignee, onClick }) => {
  return (
    <div 
      onClick={onClick}
      className="cursor-pointer group inline-block max-w-md bg-gradient-to-r from-teal-50/90 to-teal-50/70 dark:from-teal-900/40 dark:to-teal-900/20 border border-teal-200/80 dark:border-teal-700/40 rounded-md px-2.5 py-1.5 hover:from-teal-100/90 hover:to-teal-50/90 dark:hover:from-teal-800/40 dark:hover:to-teal-900/30 transition-all duration-200 shadow-sm hover:shadow-md my-0.5 mx-0.5"
    >
      <div className="flex flex-col">
        <span className="font-medium text-teal-950 dark:text-white text-xs line-clamp-1 group-hover:text-teal-800 dark:group-hover:text-teal-100 transition-colors">{summary}</span>
        <div className="flex items-center mt-0.5">
          <span className="text-[10px] text-teal-700 dark:text-teal-300">{taskKey}</span>
          <span className="text-[10px] mx-1 text-teal-400 dark:text-teal-500">•</span>
          <span className="text-[10px] text-teal-700 dark:text-teal-300">{status}</span>
          <span className="text-[10px] mx-1 text-teal-400 dark:text-teal-500">•</span>
          <span className="text-[10px] text-teal-600 dark:text-teal-400">{assignee}</span>
        </div>
      </div>
    </div>
  );
};

// Simple inline task link component
const TaskLink = ({ href, taskKey, summary, status, assignee, onClick }) => {
  return (
    <span 
      onClick={onClick}
      className="cursor-pointer group inline-flex items-center bg-teal-100/80 dark:bg-teal-800/30 hover:bg-teal-200/80 dark:hover:bg-teal-700/40 rounded px-1.5 py-0.5 text-xs border border-teal-200/70 dark:border-teal-600/40 hover:border-teal-300 dark:hover:border-teal-500/50 transition-all duration-200 mx-0.5 shadow-sm hover:shadow hover:scale-[1.01]"
    >
      <span className="font-semibold text-teal-600 dark:text-teal-300 group-hover:text-teal-700 dark:group-hover:text-teal-200 transition-colors duration-200">{taskKey}</span>
      <span className="mx-1 text-teal-400/70 dark:text-teal-500/70">•</span>
      <span className="truncate max-w-[260px] text-teal-700/90 dark:text-teal-100 group-hover:text-teal-800 dark:group-hover:text-teal-50 transition-colors duration-200">{summary}</span>
    </span>
  );
};

// Simple, clean Markdown formatter with ReactMarkdown
const MarkdownFormatter = ({ markdown }) => {
  if (!markdown) return null;
  
  const { handleLinkClick } = useAiCommandPalette();
  
  return (
    <div className="prose dark:prose-invert">
      <ReactMarkdown
        components={{
          a: ({ node, href, children }) => {
            // Check if this is a task link (/org/plan/TASK-123 format)
            const isTaskLink = href && href.includes('/plan/');
            
            if (isTaskLink) {
              // Parse children to extract the text content
              // ReactMarkdown might pass children as strings, arrays, or React elements
              let childrenText = '';
              
              // Try to extract text from various children formats
              if (typeof children === 'string') {
                childrenText = children;
              } else if (Array.isArray(children)) {
                // Flatten and join all children text content
                childrenText = children
                  .map(child => {
                    if (typeof child === 'string') return child;
                    if (child?.props?.children) {
                      if (typeof child.props.children === 'string') return child.props.children;
                      if (Array.isArray(child.props.children)) {
                        return child.props.children.map(c => 
                          typeof c === 'string' ? c : ''
                        ).join('');
                      }
                    }
                    return '';
                  })
                  .join('');
              } else if (children?.props?.children) {
                if (typeof children.props.children === 'string') {
                  childrenText = children.props.children;
                }
              }
              
              // If we have text content, try to parse the parts
              if (childrenText) {
                const parts = childrenText.split(' · ');
                
                if (parts.length >= 4) {
                  const [taskKey, summary, status, assignee] = parts;
                  
                  return (
                    <TaskLink
                      onClick={() => handleLinkClick(href)}
                      href={href}
                      taskKey={taskKey}
                      summary={summary}
                      status={status}
                      assignee={assignee}
                    />
                  );
                }
              }
            }
            
            // Default link rendering
            return (
              <button
                onClick={() => handleLinkClick(href)}
                className="bg-teal-500/10 hover:bg-teal-500/20 text-teal-500 font-medium transition-all rounded-lg px-3 py-1 text-left truncate max-w-full"
              >
                {children}
              </button>
            );
          },
          p: ({ node, children }) => (
            <p className="text-foreground mt-3.5 mb-1.5 last:mb-0">
              {children}
            </p>
          ),
          // Add styling for other markdown elements as needed
          h1: ({ node, children }) => (
            <h1 className="text-lg font-bold mt-5 mb-3 first:mt-0">{children}</h1>
          ),
          h2: ({ node, children }) => (
            <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>
          ),
          h3: ({ node, children }) => (
            <h3 className="text-md font-bold mt-3 mb-1.5">{children}</h3>
          ),
          ul: ({ node, children }) => (
            <ul className="list-disc pl-5 my-2">{children}</ul>
          ),
          ol: ({ node, children }) => (
            <ol className="list-decimal pl-5 my-2">{children}</ol>
          ),
          li: ({ node, children }) => (
            <li className="my-1">{children}</li>
          ),
          code: ({ node, inline, children }) => (
            inline 
              ? <code className="px-1 py-0.5 bg-violet-100 dark:bg-violet-900/30 rounded text-sm font-mono">{children}</code>
              : <pre className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded my-4 overflow-x-auto"><code className="text-sm font-mono">{children}</code></pre>
          ),
          blockquote: ({ node, children }) => (
            <blockquote className="border-l-4 border-violet-300 dark:border-violet-600 pl-4 italic my-4">{children}</blockquote>
          ),
          hr: ({ node, children }) => (
            <hr className="my-4 border-t border-violet-200 dark:border-violet-700/50" />
          ),
        }}
        remarkPlugins={[remarkGfm]}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

// Individual message component
const Message = ({ message, isLoading }) => {
  const { MessageTypes, loadingMessage, handleNavigateToTask } = useAiCommandPalette();
  const { user } = useAuth();
  const isUser = message.isUser;
  
  // Get avatar color with bg- prefix
  const getAvatarColorClass = () => {
    if (!user?.avatarColor) return 'bg-gray-500';
    return user.avatarColor.startsWith('bg-') ? user.avatarColor : `bg-${user.avatarColor}`;
  };
  
  // For user messages
  if (isUser) {
    return (
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center pt-1">
          <div className={`h-5 w-5 rounded-full ${getAvatarColorClass()} flex items-center justify-center text-white text-xs font-semibold`}>
            {user.name.split(' ').map(n => n[0]).join('')}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground">
            {message.text}
          </div>
        </div>
      </div>
    );
  }
  
  // For AI messages
  const aiContent = message.content;
  if (!aiContent) return null;

  return (
    <div className="flex items-start gap-3">
      <div className="flex items-center justify-center mt-4 pt-0.5">
        <StatusBotIcon className="h-5 w-5 text-teal-500" />
      </div>
      <div className="flex-1 min-w-0">
        <MarkdownFormatter markdown={aiContent} />
      </div>
    </div>
  );
};

// Loading state component that matches the message structure
const LoadingState = ({ userQuery }) => {
  const { user } = useAuth();
  
  // Get avatar color with bg- prefix
  const getAvatarColorClass = () => {
    if (!user?.avatarColor) return 'bg-gray-500';
    return user.avatarColor.startsWith('bg-') ? user.avatarColor : `bg-${user.avatarColor}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/50 dark:border-violet-500/20 p-4 flex flex-col gap-3"
    >
      {/* User message part */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center pt-1">
          <div className={`h-5 w-5 rounded-full ${getAvatarColorClass()} flex items-center justify-center text-white text-xs font-semibold`}>
            {user.name.split(' ').map(n => n[0]).join('')}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground">
            {userQuery}
          </div>
        </div>
      </div>

      {/* AI message loading part */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center mt-4">
          <StatusBotIcon className="h-5 w-5 text-teal-500" />
        </div>
        <div className="flex-1 min-w-0 mt-3.5">
          <LoadingMessage />
        </div>
      </div>
    </motion.div>
  );
};

// Create a Conversation component that groups a user message with its AI response
const Conversation = ({ userMessage, aiMessage, isLoading }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/50 dark:border-violet-500/20 p-4 flex flex-col gap-3"
    >
      <Message message={userMessage} isLoading={isLoading} />
      {aiMessage && <Message message={aiMessage} />}
    </motion.div>
  );
};

const AiResults = () => {
  const {
    isLoading,
    aiResults,
    isAskMode,
    conversationHistory
  } = useAiCommandPalette();

  if (!isAskMode && !aiResults) return null;
  
  // If no conversation yet, show a welcome message
  if (conversationHistory.length === 0) {
    return (
      <div className="p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/50 dark:border-violet-500/20 p-4"
        >
          <div className="flex flex-col gap-3 text-muted-foreground">
            <div className="text-center">
              <StatusBotIcon className="h-10 w-10 mx-auto text-violet-500 mb-2" />
              <p>Ask StatusBot a question about your tasks, projects, or anything else.</p>
            </div>
            {isLoading && (
              <div className="flex justify-center mt-4">
                <LoadingMessage />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Get the latest user message for the loading state
  const latestUserMessage = [...conversationHistory]
    .reverse()
    .find(message => message.isUser);

  // Group messages into conversations (user question + AI answer)
  const conversations = [];
  let i = 0;

  while (i < conversationHistory.length) {
    if (conversationHistory[i].isUser) {
      const userMessage = conversationHistory[i];
      const aiMessage = i + 1 < conversationHistory.length && !conversationHistory[i + 1].isUser 
        ? conversationHistory[i + 1] 
        : null;
      
      // If this is the latest user message and we're still loading and there's no AI response yet,
      // don't add it to regular conversations as we'll show it in the loading state
      const isLatestMessageStillLoading = isLoading && 
                                         userMessage.id === latestUserMessage?.id && 
                                         !aiMessage;
      
      if (!isLatestMessageStillLoading) {
        conversations.push({
          id: userMessage.id,
          userMessage,
          aiMessage
        });
      }
      
      // Skip the next message if it was an AI message
      if (aiMessage) {
        i += 2;
      } else {
        i += 1;
      }
    } else {
      // Skip orphaned AI messages
      i += 1;
    }
  }

  // Reverse the conversations array to show newest first
  const reversedConversations = [...conversations].reverse();

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        {/* Show loading state if applicable */}
        {isLoading && latestUserMessage && (
          <LoadingState userQuery={latestUserMessage.text} />
        )}

        {/* Render conversation pairs */}
        {reversedConversations.map((conv) => (
          <Conversation 
            key={conv.id}
            userMessage={conv.userMessage}
            aiMessage={conv.aiMessage}
          />
        ))}
      </div>
    </div>
  );
};

export default AiResults; 